import { t } from 'i18next';
import { useCallback } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import { RoleAssignment } from 'modules/CreateWorkspaceModal/types';
import { isEmailFormat } from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

import { canSendStateEvent } from './utils';
import { assignmentMatchesInvitee } from '../utils';

const useRoleChange = () => {
  const { matrixClient } = useMatrixContext();
  const { workspaceId, workspaceRoles, roleAssignments } = useWorkspacesContext({ requireWorkspaceId: false });
  const { pushToast } = useToast();

  const changeRole = useCallback(
    async (matrixId: string, newRoleId: string, shouldShowSuccessToast = true, targetWorkspaceId?: string) => {
      const effectiveWorkspaceId = targetWorkspaceId ?? workspaceId;
      if (!effectiveWorkspaceId) return;

      const currentUserId = matrixClient.getUserId();
      const workspaceRoom = matrixClient.getRoom(effectiveWorkspaceId);
      if (!workspaceRoom || !currentUserId) return;

      const isEmail = isEmailFormat(matrixId);
      const currentRole = roleAssignments.find((assignment) => assignmentMatchesInvitee(assignment, matrixId));
      const newRole = workspaceRoles.find(({ id }) => id === newRoleId);

      if (currentRole?.roleId === newRoleId) return;

      try {
        if (!newRole) throw new Error(`Role ${newRoleId} not found`);

        const permissions = calculatePermissions(matrixClient, effectiveWorkspaceId);

        if (!permissions.adjustRoles) {
          return pushToast({
            variant: 'info',
            title: t('workspaces.no_permission'),
            description: t('workspaces.no_permission_description'),
          });
        }

        const canSendAssignment = canSendStateEvent(
          workspaceRoom,
          currentUserId,
          CustomMeedioMatrixEvents.WORKSPACE_ROLES
        );

        if (!canSendAssignment) {
          return pushToast({
            variant: 'info',
            title: t('workspaces.no_permission'),
            description: t('workspaces.no_permission_description'),
          });
        }

        const newAssignment: RoleAssignment = isEmail
          ? { matrixId: null, pendingEmail: matrixId, roleId: newRoleId, assignedBy: currentUserId }
          : { matrixId, roleId: newRoleId, assignedBy: currentUserId };

        const updatedRoleAssignments: RoleAssignment[] = currentRole
          ? roleAssignments.map((member) =>
              assignmentMatchesInvitee(member, matrixId) ? { ...member, roleId: newRoleId } : member
            )
          : [...roleAssignments, newAssignment];

        await matrixClient.sendStateEvent(effectiveWorkspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
          version: WORKSPACE_ROLES_EVENT_VERSION,
          roles: workspaceRoles,
          assignments: updatedRoleAssignments,
        });

        if (shouldShowSuccessToast) pushToast({ variant: 'success', title: t('workspaces_role_update_success') });
      } catch (error) {
        logger.error('Failed to assign workspace role', {
          error,
          workspaceId: effectiveWorkspaceId,
          memberId: matrixId,
          newRoleId,
        });
        pushToast({
          variant: 'warning',
          title: t('workspaces_role_update_fail'),
          description: t('try_again_and_reload'),
        });
      }
    },
    [matrixClient, pushToast, roleAssignments, workspaceId, workspaceRoles]
  );

  return changeRole;
};

export default useRoleChange;
